import React from "react";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";

/**
 * KPI card. Six pastel tones + primary/accent/muted/default.
 * Pastel tones use bespoke tokens (see index.css & tailwind.config.js).
 */
const TONE_CLASSES = {
  default:  { card: "bg-white",                                                 icon: "text-primary",      overline: "text-muted-foreground", value: "text-primary",     unit: "text-muted-foreground", trend: "text-secondary" },
  primary:  { card: "bg-primary text-primary-foreground border-primary",        icon: "text-primary-foreground", overline: "text-primary-foreground/70", value: "text-primary-foreground", unit: "text-primary-foreground/70", trend: "text-primary-foreground/70" },
  accent:   { card: "bg-accent text-accent-foreground border-accent",           icon: "text-accent-foreground",  overline: "text-accent-foreground/80",  value: "text-accent-foreground",  unit: "text-accent-foreground/80",  trend: "text-accent-foreground/80" },
  muted:    { card: "bg-muted",                                                 icon: "text-primary",      overline: "text-muted-foreground", value: "text-primary",     unit: "text-muted-foreground", trend: "text-secondary" },
  // Pastels — soft backgrounds with harmonised text/border tokens
  mint:     { card: "bg-pastel-mint-bg border-pastel-mint-border",              icon: "text-pastel-mint-fg",     overline: "text-pastel-mint-fg/80",     value: "text-pastel-mint-fg",     unit: "text-pastel-mint-fg/70",     trend: "text-pastel-mint-fg/80" },
  sky:      { card: "bg-pastel-sky-bg border-pastel-sky-border",                icon: "text-pastel-sky-fg",      overline: "text-pastel-sky-fg/80",      value: "text-pastel-sky-fg",      unit: "text-pastel-sky-fg/70",      trend: "text-pastel-sky-fg/80" },
  peach:    { card: "bg-pastel-peach-bg border-pastel-peach-border",            icon: "text-pastel-peach-fg",    overline: "text-pastel-peach-fg/80",    value: "text-pastel-peach-fg",    unit: "text-pastel-peach-fg/70",    trend: "text-pastel-peach-fg/80" },
  lavender: { card: "bg-pastel-lavender-bg border-pastel-lavender-border",      icon: "text-pastel-lavender-fg", overline: "text-pastel-lavender-fg/80", value: "text-pastel-lavender-fg", unit: "text-pastel-lavender-fg/70", trend: "text-pastel-lavender-fg/80" },
  lemon:    { card: "bg-pastel-lemon-bg border-pastel-lemon-border",            icon: "text-pastel-lemon-fg",    overline: "text-pastel-lemon-fg/80",    value: "text-pastel-lemon-fg",    unit: "text-pastel-lemon-fg/70",    trend: "text-pastel-lemon-fg/80" },
  rose:     { card: "bg-pastel-rose-bg border-pastel-rose-border",              icon: "text-pastel-rose-fg",     overline: "text-pastel-rose-fg/80",     value: "text-pastel-rose-fg",     unit: "text-pastel-rose-fg/70",     trend: "text-pastel-rose-fg/80" },
};

export default function KpiCard({ label, value, unit, trend, icon: Icon, tone = "default", testid }) {
  const t = TONE_CLASSES[tone] || TONE_CLASSES.default;
  return (
    <div
      className={cn(
        "rounded-lg border p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm group",
        t.card
      )}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className={cn("text-[10px] uppercase tracking-[0.14em] font-medium", t.overline)}>{label}</div>
        {Icon && <Icon size={16} className={cn("opacity-90", t.icon)} />}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className={cn("text-3xl md:text-4xl font-semibold tracking-tight", t.value)}>{value}</span>
        {unit && <span className={cn("text-sm", t.unit)}>{unit}</span>}
      </div>
      {trend && (
        <div className={cn("mt-2 flex items-center gap-1 text-xs", t.trend)}>
          <ArrowUpRight size={12} />
          <span>{trend}</span>
        </div>
      )}
    </div>
  );
}
